import { useState, useEffect, useMemo } from 'react'
import Mustache from 'mustache'
import {
  sendInviteEmails,
  getTemplates,
  getTemplate,
  getSenderSignatures,
  type InviteSummary,
  type TemplateInfo,
  type TemplateDetail,
  type SenderSignature,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'

// Variables that are auto-filled per invite recipient
const AUTO_FILLED_VARS = ['name', 'email', 'recipient_name', 'recipient_email', 'invite_url', 'invite_token', 'invite_link', 'action_url']

// Infrastructure variables (less important, shown collapsed)
const INFRA_VARS = ['averia_regular_url', 'averia_bold_url', 'mona_medium_url', 'mona_bold_url']

interface Props {
  invites: InviteSummary[]
  onClose: () => void
  onSent: () => void
}

export function SendInvitesModal({ invites, onClose, onSent }: Props) {
  const [templates, setTemplates] = useState<TemplateInfo[]>([])
  const [senderSignatures, setSenderSignatures] = useState<SenderSignature[]>([])
  const [templateKey, setTemplateKey] = useState('')
  const [fromEmail, setFromEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [baseUrl, setBaseUrl] = useState('https://barta.shop/invite')
  const [templatePreview, setTemplatePreview] = useState<TemplateDetail | null>(null)
  const [templateVars, setTemplateVars] = useState<Record<string, string>>({})
  const [showPreview, setShowPreview] = useState(false)
  const [loadingData, setLoadingData] = useState(true)
  const [loadingTemplate, setLoadingTemplate] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ sent: string[]; failed: { email: string; error: string }[]; skipped: { id: string; reason: string }[] } | null>(null)

  const eligibleInvites = invites.filter((i) => i.status === 'pending' && i.email && !i.email_sent_at)

  const selectStyles = `w-full rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] text-gray-900 dark:text-gray-100 px-4 py-2.5 text-base transition-all duration-200 focus:border-[#503864] dark:focus:border-[#7f4f9a] focus:outline-none focus:ring-2 focus:ring-[#503864]/20 dark:focus:ring-[#7f4f9a]/20`

  // Load templates and sender signatures on mount
  useEffect(() => {
    async function loadData() {
      setLoadingData(true)
      try {
        const [templatesRes, signaturesRes] = await Promise.all([
          getTemplates().catch(() => ({ ok: false, data: [] })),
          getSenderSignatures().catch(() => ({ ok: false, data: { signatures: [] } })),
        ])
        if (templatesRes.ok && templatesRes.data) {
          setTemplates(templatesRes.data)
          if (templatesRes.data.length > 0) {
            setTemplateKey(templatesRes.data[0].alias)
          }
        } else {
          setError('Failed to load templates. Check Postmark configuration.')
        }
        if (signaturesRes.ok && signaturesRes.data) {
          // Handle both { signatures: [...] } and bare array responses
          const sigs = Array.isArray(signaturesRes.data)
            ? signaturesRes.data
            : signaturesRes.data.signatures ?? []
          setSenderSignatures(sigs)
          if (sigs.length > 0) {
            setFromEmail(sigs[0].email)
          }
        }
      } catch {
        setError('Failed to load form data')
      } finally {
        setLoadingData(false)
      }
    }
    loadData()
  }, [])

  // Auto-load template details when template changes
  useEffect(() => {
    if (!templateKey) {
      setTemplatePreview(null)
      return
    }
    async function loadTemplate() {
      setLoadingTemplate(true)
      try {
        const response = await getTemplate(templateKey)
        if (response.ok && response.data) {
          setTemplatePreview(response.data)
          if (response.data.template_model) {
            // Defaults for invite emails
            const inviteDefaults: Record<string, string> = {
              headline: "You've been selected for early access",
              subheadline: 'Set up your shop before we open the doors to everyone else.',
              preheader: 'Your seller invite to Barta is waiting — claim your spot before it expires.',
            }
            const initialVars: Record<string, string> = {}
            for (const [key, value] of Object.entries(response.data.template_model)) {
              initialVars[key] = templateVars[key] !== undefined
                ? templateVars[key]
                : inviteDefaults[key] ?? String(value || '')
            }
            setTemplateVars(initialVars)
          }
        }
      } catch {
        console.error('Failed to load template details')
      } finally {
        setLoadingTemplate(false)
      }
    }
    loadTemplate()
  }, [templateKey])

  // Render template with current variable values
  const renderedHtml = useMemo(() => {
    if (!templatePreview?.html_body) return ''
    try {
      const context: Record<string, unknown> = {}

      // Add template defaults first
      if (templatePreview.template_model) {
        for (const [key, defaultValue] of Object.entries(templatePreview.template_model)) {
          context[key] = templateVars[key] || defaultValue || ''
        }
      }

      // Override with user-provided values
      for (const [key, value] of Object.entries(templateVars)) {
        if (value) context[key] = value
      }

      // Add sample values for auto-filled invite vars
      context.name = 'Jane Doe'
      context.email = 'jane@example.com'
      context.recipient_name = 'Jane Doe'
      context.recipient_email = 'jane@example.com'
      context.invite_url = `${baseUrl}/abc123`
      context.invite_token = 'abc123'
      context.invite_link = `${baseUrl}/abc123`
      context.action_url = `${baseUrl}/abc123`

      return Mustache.render(templatePreview.html_body, context)
    } catch {
      return templatePreview.html_body
    }
  }, [templatePreview?.html_body, templatePreview?.template_model, templateVars, baseUrl])

  const handleSend = async () => {
    if (!fromEmail) {
      setError('Please select a sender email')
      return
    }
    if (!templateKey) {
      setError('Select a template')
      return
    }
    if (!baseUrl) {
      setError('Enter a base URL')
      return
    }

    setSending(true)
    setError(null)
    try {
      // Filter out auto-filled and empty vars
      const filteredVars: Record<string, string> = {}
      for (const [key, value] of Object.entries(templateVars)) {
        if (value && !AUTO_FILLED_VARS.includes(key.toLowerCase())) {
          filteredVars[key] = value
        }
      }

      const response = await sendInviteEmails({
        invite_ids: eligibleInvites.map((i) => i.id),
        template_key: templateKey,
        base_url: baseUrl,
        from_email: fromEmail,
        subject: subject || undefined,
        template_vars: Object.keys(filteredVars).length > 0 ? filteredVars : undefined,
      })
      if (response.ok && response.data) {
        setResult(response.data)
        onSent()
      } else {
        setError(response.error?.message || 'Failed to send invite emails')
      }
    } catch {
      setError('Failed to connect to API')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white dark:bg-[#1a1520] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-6">
          <h2 className="font-heading text-xl font-bold text-gray-900 dark:text-gray-100">Send Invite Emails</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-xl bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">{error}</div>
        )}

        {result ? (
          <div className="space-y-3">
            {result.sent.length > 0 && (
              <div className="rounded-xl bg-green-50 dark:bg-green-900/20 p-3 text-sm text-green-700 dark:text-green-300">
                Sent to {result.sent.length} recipient(s): {result.sent.join(', ')}
              </div>
            )}
            {result.failed.length > 0 && (
              <div className="rounded-xl bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
                Failed: {result.failed.map((f) => `${f.email} (${f.error})`).join(', ')}
              </div>
            )}
            {result.skipped.length > 0 && (
              <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-700 dark:text-amber-300">
                Skipped {result.skipped.length}: {result.skipped.map((s) => s.reason).join(', ')}
              </div>
            )}
            <CustomButton onClick={onClose} variant="primary">
              Done
            </CustomButton>
          </div>
        ) : loadingData ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Sending emails to {eligibleInvites.length} pending invite(s) with email addresses.
            </p>

            {eligibleInvites.length === 0 ? (
              <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-700 dark:text-amber-300">
                No eligible invites to send. Invites must be pending and have an email address.
              </div>
            ) : (
              <>
                {/* From Email */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    From Email <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={fromEmail}
                    onChange={(e) => setFromEmail(e.target.value)}
                    className={selectStyles}
                    required
                  >
                    <option value="">Select sender...</option>
                    {senderSignatures.map((sig) => (
                      <option key={sig.email} value={sig.email}>
                        {sig.name ? `${sig.name} <${sig.email}>` : sig.email}
                      </option>
                    ))}
                  </select>
                  {senderSignatures.length === 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      No verified senders found. Add sender signatures in Postmark dashboard.
                    </p>
                  )}
                </div>

                {/* Email Template */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email Template <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={templateKey}
                    onChange={(e) => {
                      setTemplateKey(e.target.value)
                      setShowPreview(false)
                    }}
                    className={selectStyles}
                  >
                    <option value="">Select a template...</option>
                    {templates.map((t) => (
                      <option key={t.alias} value={t.alias}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  {loadingTemplate && (
                    <p className="text-xs text-gray-500 mt-1">Loading template details...</p>
                  )}
                  {templatePreview && (
                    <p className="text-xs text-gray-500 mt-1">
                      Template subject: <span className="font-medium">{templatePreview.subject}</span>
                    </p>
                  )}
                  {templates.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      No templates found. Create templates in Postmark dashboard.
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <CustomTextInput
                    label="Subject (optional)"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder={templatePreview?.subject || 'Uses template subject if empty'}
                  />
                  {templatePreview && !subject && (
                    <p className="text-xs text-gray-500">
                      Will use template subject: "{templatePreview.subject}"
                    </p>
                  )}
                </div>

                <CustomTextInput
                  label="Base URL for invite links"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://barta.shop/invite"
                />

                {/* Template Variables */}
                {templatePreview?.template_model && Object.keys(templatePreview.template_model).length > 0 && (() => {
                  const entries = Object.entries(templatePreview.template_model)
                  const contentVars = entries.filter(([key]) => !INFRA_VARS.includes(key))
                  const infraVars = entries.filter(([key]) => INFRA_VARS.includes(key))

                  return (
                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Template Variables
                      </h4>
                      <div className="space-y-3">
                        {contentVars.map(([key, defaultValue]) => {
                          const isAutoFilled = AUTO_FILLED_VARS.includes(key.toLowerCase())
                          return (
                            <div key={key} className="flex items-center gap-3">
                              <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-xs min-w-[140px] shrink-0">
                                {`{{${key}}}`}
                              </code>
                              {isAutoFilled ? (
                                <span className="text-xs text-green-600 dark:text-green-400 italic">
                                  Auto-filled per recipient
                                </span>
                              ) : (
                                <input
                                  type="text"
                                  value={templateVars[key] ?? ''}
                                  onChange={(e) => setTemplateVars(prev => ({ ...prev, [key]: e.target.value }))}
                                  placeholder={String(defaultValue) || `Enter ${key}...`}
                                  className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm"
                                />
                              )}
                            </div>
                          )
                        })}
                      </div>

                      {/* Infrastructure variables (collapsed by default) */}
                      {infraVars.length > 0 && (
                        <details className="mt-4">
                          <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                            Advanced: Font & asset URLs ({infraVars.length})
                          </summary>
                          <div className="mt-2 space-y-2 pl-2 border-l-2 border-gray-200">
                            {infraVars.map(([key, defaultValue]) => (
                              <div key={key} className="flex items-center gap-2">
                                <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs">
                                  {key}
                                </code>
                                <input
                                  type="text"
                                  value={templateVars[key] ?? ''}
                                  onChange={(e) => setTemplateVars(prev => ({ ...prev, [key]: e.target.value }))}
                                  placeholder={String(defaultValue).slice(0, 50) + '...'}
                                  className="flex-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-xs"
                                />
                              </div>
                            ))}
                          </div>
                        </details>
                      )}

                      <div className="mt-3 flex items-center justify-between">
                        <p className="text-xs text-gray-500">
                          Empty fields use template defaults.
                        </p>
                        <CustomButton
                          onClick={() => setShowPreview(!showPreview)}
                          variant="ghost"
                          size="sm"
                        >
                          {showPreview ? 'Hide Preview' : 'Show Preview'}
                        </CustomButton>
                      </div>
                    </div>
                  )
                })()}

                {/* Email Preview */}
                {showPreview && templatePreview && (
                  <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 px-4 py-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Email Preview
                      </span>
                      <button
                        onClick={() => setShowPreview(false)}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="max-h-80 overflow-auto bg-white">
                      <iframe
                        srcDoc={renderedHtml}
                        title="Template Preview"
                        className="w-full h-80 border-0"
                        style={{ background: 'white' }}
                      />
                    </div>
                  </div>
                )}

                <div className="rounded-xl bg-gray-50 dark:bg-[#0f0d12] p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Recipients:</p>
                  <div className="space-y-1">
                    {eligibleInvites.map((inv) => (
                      <p key={inv.id} className="text-sm text-gray-700 dark:text-gray-300">{inv.email}</p>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <CustomButton onClick={handleSend} variant="primary" loading={sending} disabled={!fromEmail}>
                    Send {eligibleInvites.length} Email(s)
                  </CustomButton>
                  <CustomButton onClick={onClose} variant="secondary">
                    Cancel
                  </CustomButton>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
